import {
  Color,
  Vector3,
  DoubleSide,
  InstancedBufferGeometry,
  Mesh,
  MeshStandardMaterial,
  Texture,
  type PerspectiveCamera,
  type WebGLProgramParametersWithUniforms,
} from 'three';
import { GRASS, METERS_PER_TEXEL, TERRAIN_LOOK, WORLD } from '../config';
import { buildBladeGeometry } from './grassGeometry';

/**
 * Pole 3D stébel trávy kolem kamery.
 *
 * Zásadní vlastnost: **CPU o trávě neví nic**. Nejsou tu žádné instanční
 * matice, žádné přepočítávání při pohybu, žádné alokace za běhu. Pozice
 * každého stébla se odvodí ve vertex shaderu z `gl_InstanceID`:
 *
 *   instance -> (buňka mřížky, pořadí v buňce)
 *   buňka    -> celočíselná světová souřadnice odvozená od pozice kamery
 *   stéblo   -> hash té celočíselné souřadnice
 *
 * Protože pozice závisí na *celočíselné světové buňce*, je tráva ukotvená
 * ve světě — při chůzi se buňky na okraji samy recyklují a nic neplave.
 * Celé pole je jeden draw call.
 *
 * Výšku, sklon a masku porostu bere stéblo ze stejné výškové mapy jako
 * terén, takže tráva roste jen tam, kde ji terén skutečně maluje —
 * ne na skále, ne pod vodou a ne ve sněhu.
 */
export class Grass {
  readonly mesh: Mesh;
  private readonly uniforms = {
    uTime: { value: 0 },
    uCameraCell: { value: [0, 0] as [number, number] },
    uWindStrength: { value: GRASS.windStrength },
    uWindSpeed: { value: GRASS.windSpeed },
    uBladeHeight: { value: GRASS.bladeHeight },
    uBladeWidth: { value: GRASS.bladeWidth },
    uQuality: { value: 1 },
    // Barvy jsou v LINEÁRNÍM prostoru. Původní hodnoty odpovídaly v sRGB
    // skoro černé a stébla vypadala jako tmavé dráty.
    uTintDeep: { value: new Color(0.030, 0.055, 0.016) },
    uTintTip: { value: new Color(0.150, 0.235, 0.058) },
    uSunDir: { value: new Vector3(0, 1, 0) },
    uSunColor: { value: new Color(1.0, 0.96, 0.86) },
  };

  constructor(heightMap: Texture, grassAlbedo: Texture, setupShadows?: (m: MeshStandardMaterial) => void) {
    const instanceCount = GRASS.gridSize * GRASS.gridSize * GRASS.bladesPerCell;
    const geometry = buildBladeGeometry(GRASS.segments, instanceCount);

    const material = new MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.96,
      metalness: 0.0,
      side: DoubleSide,
    });

    setupShadows?.(material);
    const previous = material.onBeforeCompile;
    const uniforms = this.uniforms;

    material.onBeforeCompile = function (shader: WebGLProgramParametersWithUniforms, renderer) {
      previous.call(material, shader, renderer);

      Object.assign(shader.uniforms, uniforms, {
        uHeightMap: { value: heightMap },
        uGrassAlbedo: { value: grassAlbedo },
        uHeightRes: { value: WORLD.heightRes },
        uWorldSize: { value: WORLD.size },
        uTexel: { value: METERS_PER_TEXEL },
        uCellSize: { value: GRASS.cellSize },
        uGridSize: { value: GRASS.gridSize },
        uBladesPerCell: { value: GRASS.bladesPerCell },
        uFadeStart: { value: GRASS.fadeStart },
        uFadeEnd: { value: GRASS.fadeEnd },
        uMidDistance: { value: GRASS.midDistance },
        uAlbedoScale: { value: 1 / TERRAIN_LOOK.tileGrass },
        uMinHeight: { value: WORLD.seaLevel + TERRAIN_LOOK.beachHeight },
        uMaxHeight: { value: TERRAIN_LOOK.snowStart },
      });

      shader.vertexShader = VERT_HEAD + shader.vertexShader;
      shader.vertexShader = shader.vertexShader
        .replace('#include <beginnormal_vertex>', VERT_PLACE)
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
          transformed = gBladePosition;`,
        );

      shader.fragmentShader = FRAG_HEAD + shader.fragmentShader;
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <roughnessmap_fragment>',
          `#include <roughnessmap_fragment>
          // Ne úplně matné. Stéblo má voskový povrch a po délce chytá
          // ostrý odlesk — při 0.96 nechytí nic a vypadá jako plastelína.
          roughnessFactor = mix(0.52, 0.80, vBladeHeightFrac);`,
        )
        .replace(
          '#include <normal_fragment_begin>',
          `#include <normal_fragment_begin>
          // Obě strany stébla musí být osvětlené.
          //
          // Stéblo je nekonečně tenké, takže jeho "rub" žádný rub není —
          // je to pořád tentýž list nasvícený z druhé strany. Bez otočení
          // normály vyjde odvrácená polovina stébel skoro černá a v poli
          // se objeví tmavé tyčky mezi světlými.
          normal = gl_FrontFacing ? normal : -normal;
          // Difuzní světlo se navíc ohýbá kolem tenkého listu (wrap
          // lighting) — bez toho je přechod světlo/stín na stéble tvrdý
          // jako na plechu.
          normal = normalize(mix(normal, vec3(0.0, 1.0, 0.0), 0.22));`,
        )
        .replace('#include <opaque_fragment>', FRAG_TRANSLUCENCY + '#include <opaque_fragment>')
        .replace(
          '#include <map_fragment>',
          `#include <map_fragment>
          if (vFade < 0.01) discard;
          diffuseColor.rgb *= vTint;`,
        )
        .replace(
          '#include <alphatest_fragment>',
          `#include <alphatest_fragment>
          // Vzdálená stébla se rozpouští ditherem místo průhlednosti —
          // nemusí do transparentní fronty a nerozbíjí řazení.
          if (gDither(gl_FragCoord.xy) > vFade) discard;`,
        );
    };

    this.mesh = new Mesh(geometry, material);
    this.mesh.name = 'grass';
    this.mesh.frustumCulled = false;
    this.mesh.receiveShadow = true;
    this.mesh.castShadow = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.updateMatrix();
  }

  update(elapsed: number, camera: PerspectiveCamera, sunDirection?: Vector3): void {
    this.uniforms.uTime.value = elapsed;
    // Uniform míří KE slunci; sunDirection míří od něj dolů.
    if (sunDirection) this.uniforms.uSunDir.value.copy(sunDirection).negate().normalize();
    this.uniforms.uCameraCell.value[0] = Math.floor(camera.position.x / GRASS.cellSize);
    this.uniforms.uCameraCell.value[1] = Math.floor(camera.position.z / GRASS.cellSize);
  }

  setQuality(value: number): void {
    this.uniforms.uQuality.value = Math.min(1, Math.max(0.25, value));
  }

  get quality(): number {
    return this.uniforms.uQuality.value;
  }

  get instanceCount(): number {
    return (this.mesh.geometry as InstancedBufferGeometry).instanceCount;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as MeshStandardMaterial).dispose();
  }
}

const VERT_HEAD = /* glsl */ `
uniform sampler2D uHeightMap;
uniform sampler2D uGrassAlbedo;
uniform float uHeightRes;
uniform float uWorldSize;
uniform float uTexel;
uniform float uTime;
uniform vec2 uCameraCell;
uniform float uCellSize;
uniform float uGridSize;
uniform float uBladesPerCell;
uniform float uFadeStart;
uniform float uFadeEnd;
uniform float uMidDistance;
uniform float uAlbedoScale;
uniform float uMinHeight;
uniform float uMaxHeight;
uniform float uQuality;
uniform float uWindStrength;
uniform float uWindSpeed;
uniform float uBladeHeight;
uniform float uBladeWidth;
uniform vec3 uTintDeep;
uniform vec3 uTintTip;
uniform vec3 uSunDir;
uniform vec3 uSunColor;

attribute float aSide;
attribute float aHeight;

varying float vFade;
varying vec3 vTint;
varying float vBladeHeightFrac;
varying vec3 vWorldPos;
varying vec3 vWorldNormal;

vec3 gBladePosition;

float gHash(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
vec2 gHash2(vec2 p) {
  return vec2(gHash(p), gHash(p + 19.19));
}
float gNoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(gHash(i), gHash(i + vec2(1.0, 0.0)), u.x),
             mix(gHash(i + vec2(0.0, 1.0)), gHash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float gFbm(vec2 p) {
  float s = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) { s += a * gNoise(p); p *= 2.07; a *= 0.5; }
  return s;
}

float gTerrainHeight(vec2 wxz) {
  vec2 t = (wxz + uWorldSize * 0.5) / uTexel;
  vec2 f = fract(t);
  ivec2 i0 = ivec2(floor(t));
  ivec2 m = ivec2(int(uHeightRes) - 1);
  float h00 = texelFetch(uHeightMap, clamp(i0, ivec2(0), m), 0).r;
  float h10 = texelFetch(uHeightMap, clamp(i0 + ivec2(1, 0), ivec2(0), m), 0).r;
  float h01 = texelFetch(uHeightMap, clamp(i0 + ivec2(0, 1), ivec2(0), m), 0).r;
  float h11 = texelFetch(uHeightMap, clamp(i0 + ivec2(1, 1), ivec2(0), m), 0).r;
  return mix(mix(h00, h10, f.x), mix(h01, h11, f.x), f.y);
}
`;

const VERT_PLACE = /* glsl */ `
#include <beginnormal_vertex>

// --- instance -> svetova bunka ---
int gInstance = gl_InstanceID;
int gPerCell = int(uBladesPerCell);
int gCell = gInstance / gPerCell;
int gSub = gInstance - gCell * gPerCell;
int gGrid = int(uGridSize);
vec2 gLocal = vec2(float(gCell - (gCell / gGrid) * gGrid), float(gCell / gGrid)) - uGridSize * 0.5;
vec2 gCellCoord = uCameraCell + gLocal;

// Pozice stebla zavisi jen na celociselne bunce, takze je ukotvena ve svete.
vec2 gJitter = gHash2(gCellCoord + float(gSub) * 7.13);
vec2 gWorldXZ = (gCellCoord + gJitter) * uCellSize;

float gDist = distance(gWorldXZ, cameraPosition.xz);
float gFadeStart = uFadeStart * uQuality;
float gFadeEnd = uFadeEnd * uQuality;
float gMid = uMidDistance * uQuality;

float gAllowed = mix(uBladesPerCell, 1.0, smoothstep(gMid, gFadeEnd, gDist));
float gKeep = float(gSub) < gAllowed ? 1.0 : 0.0;
float gFade = (1.0 - smoothstep(gFadeStart, gFadeEnd, gDist)) * gKeep;

vFade = 0.0;
vTint = vec3(0.0);
vBladeHeightFrac = aHeight;
gBladePosition = vec3(0.0);

if (gFade > 0.001) {
  float gGroundY = gTerrainHeight(gWorldXZ);

  // Normala terenu z centralnich diferenci -- steblo musi stat kolmo ke svahu.
  float gE = uTexel;
  vec3 gGroundNormal = normalize(vec3(
    gTerrainHeight(gWorldXZ - vec2(gE, 0.0)) - gTerrainHeight(gWorldXZ + vec2(gE, 0.0)),
    2.0 * gE,
    gTerrainHeight(gWorldXZ - vec2(0.0, gE)) - gTerrainHeight(gWorldXZ + vec2(0.0, gE))
  ));

  // --- maska porostu a shlukovani (clumping) ---
  // Tráva roste i na svazích. Práh 0.72 znamenal, že všechno strmější než
  // zhruba 28° zůstalo holé — jenže terén tam pořád maluje travnatou
  // texturu, takže na svahu chyběla stébla, ale zeleň zůstala. Odtud ten
  // dojem, že tráva někde je a někde ne.
  float gSlopeOk = smoothstep(0.52, 0.74, gGroundNormal.y);
  float gBandOk = smoothstep(uMinHeight - 2.0, uMinHeight + 6.0, gGroundY)
                * (1.0 - smoothstep(uMaxHeight - 25.0, uMaxHeight, gGroundY));

  float macro = gFbm(gWorldXZ * 0.0035);
  float meso = gFbm(gWorldXZ * 0.045);
  float clump = gFbm(gWorldXZ * 0.12);

  // Prirozene louky a trsy
  float gPatch = smoothstep(0.20, 0.65, clump);
  float gMask = gSlopeOk * gBandOk * (0.58 + 0.42 * gPatch);

  vFade = gFade * step(0.02, gMask);

  float gRand = gHash(gCellCoord * 1.7 + float(gSub) * 3.31);
  float gRand2 = gHash(gCellCoord * 2.9 + float(gSub) * 5.77);
  float gRand3 = gHash(gCellCoord * 4.3 + float(gSub) * 8.19);

  float gAlive = step(0.001, vFade);
  // Bohatsi variace vysek a prirozene trsy
  float gHeightScale = mix(0.55, 1.35, gRand) * (0.7 + 0.5 * clump) * uBladeHeight * mix(0.75, 1.0, gMask) * gAlive;
  float gWidthScale = uBladeWidth * mix(0.85, 1.4, gRand2) * mix(1.0, 1.6, smoothstep(gMid * 0.4, gFadeEnd, gDist)) * gAlive;

  float gAngle = gRand2 * 6.28318;
  vec2 gTangent = vec2(-sin(gAngle), cos(gAngle));

  // --- vitr a prirozene prohnuti stebla ---
  float gGust = gNoise(gWorldXZ * 0.035 + vec2(uTime * uWindSpeed * 0.25, 0.0));
  float gFlutter = sin(uTime * uWindSpeed * 2.6 + gRand * 6.28 + gWorldXZ.x * 0.7);
  float gNaturalCurve = mix(0.15, 0.45, gRand3);
  float gBendAmount = gNaturalCurve + uWindStrength * (0.25 + gGust * 0.75) + gFlutter * 0.04;

  float t = aHeight;
  float gBend = gBendAmount * t * t;
  float gVertical = gHeightScale * t * cos(gBend);
  float gLean = gHeightScale * t * sin(gBend);

  vec3 gUp = gGroundNormal;
  vec3 gAcross = normalize(cross(gUp, vec3(gTangent.x, 0.0, gTangent.y)) + vec3(1e-5, 0.0, 0.0));
  vec3 gForward = cross(gAcross, gUp);

  gBladePosition = vec3(gWorldXZ.x, gGroundY, gWorldXZ.y)
                 + gUp * gVertical
                 + gForward * gLean
                 + gAcross * (position.x * gWidthScale);

  // Víc zaoblení do stran. Když normála míří převážně vzhůru, chytají
  // všechna stébla světlo stejně a pole vypadá jako plochý koberec —
  // tvar stébla se čte právě z toho, jak se odlesk mění po jeho šířce.
  objectNormal = normalize(gForward * 0.15 + gAcross * aSide * 0.78 + gUp * 0.58);

  // --- barva stébla ---
  //
  // Barva podkladu se do stébla **nemíchá**. Dřív tvořila třicet procent
  // výsledku a stébla se tím hodnotově srovnala se zemí pod sebou — pole
  // pak splývalo v jednu zelenou kaši, ve které nešlo rozeznat jednotlivá
  // stébla. Tráva má vlastní barvu; podklad se smí projevit nanejvýš jako
  // mírné ztmavení.
  float ao = mix(0.42, 1.0, pow(t, 0.55));

  vec3 gBase = mix(uTintDeep, uTintTip, pow(t, 0.75));

  // Uschlá stébla. V každém trsu jich pár je a bez nich vypadá louka
  // jako umělý trávník.
  // Uschlá stébla: jen několik procent porostu. Když je jich moc, mění se
  // louka v seno.
  vec3 gDry = vec3(0.185, 0.150, 0.045);
  gBase = mix(gBase, gDry, smoothstep(0.80, 1.0, gRand3) * mix(0.15, 0.8, t));

  // Odstín se mění po ploše: někde do modrozelena, jinde do žlutozelena.
  gBase *= mix(vec3(0.88, 1.04, 0.94), vec3(1.10, 1.00, 0.80), macro);
  gBase *= 0.86 + 0.28 * meso;
  gBase *= mix(0.88, 1.18, gRand);

  vTint = gBase * ao;
  vBladeHeightFrac = t;
  vWorldPos = gBladePosition;
  vWorldNormal = objectNormal;
}
`;

/**
 * Prosvítání stébla proti slunci.
 *
 * Tohle odlišuje trávu od zelených tyček. Stéblo je tenké a světlo jím
 * prochází: když stojí slunce za porostem, celá louka se rozzáří dozlatova
 * a jednotlivá stébla je vidět jako světlé čárky proti tmavému pozadí.
 * Bez toho zůstane pole ploché za každého osvětlení.
 *
 * Sílí to ke špičce, protože tam je stéblo nejtenčí.
 */
const FRAG_TRANSLUCENCY = /* glsl */ `
{
  vec3 gView = normalize(cameraPosition - vWorldPos);
  // Světlo prochází stéblem, když míří zhruba proti pohledu.
  float gBack = pow(max(dot(gView, -uSunDir), 0.0), 3.5);
  // Kolmé stéblo prosvítá víc než to natočené plochou k nám.
  float gEdge = 1.0 - abs(dot(gView, normalize(vWorldNormal)));
  float gThin = mix(0.25, 1.0, vBladeHeightFrac);
  float gSun = max(uSunDir.y, 0.0);
  outgoingLight += uSunColor * vTint * gBack * gThin * (0.45 + 0.55 * gEdge) * gSun * 5.5;
}
`;

const FRAG_HEAD = /* glsl */ `
uniform vec3 uSunDir;
uniform vec3 uSunColor;
varying float vFade;
varying vec3 vTint;
varying float vBladeHeightFrac;
varying vec3 vWorldPos;
varying vec3 vWorldNormal;

// Uspořádaný dither 4x4 — plynulé mizení bez průhlednosti a bez řazení.
float gDither(vec2 fragCoord) {
  int x = int(mod(fragCoord.x, 4.0));
  int y = int(mod(fragCoord.y, 4.0));
  int index = x + y * 4;
  float table[16];
  table[0]=0.0625; table[1]=0.5625; table[2]=0.1875; table[3]=0.6875;
  table[4]=0.8125; table[5]=0.3125; table[6]=0.9375; table[7]=0.4375;
  table[8]=0.25;   table[9]=0.75;   table[10]=0.125; table[11]=0.625;
  table[12]=1.0;   table[13]=0.5;   table[14]=0.875; table[15]=0.375;
  return table[index];
}
`;
